import { useCallback, useEffect, useRef, useState } from "react";
import * as Sentry from "@Sentry";
import { getChatDirectoryRequest, type ChatDirectoryUser } from "../api/portalChat.api";
import { DIRECTORY_SEARCH_DEBOUNCE_MS, DIRECTORY_PAGE_SIZE } from "../portalChat.constants";

/**
 * Server-paginated chat directory for the people pickers. The first page loads immediately
 * (debounced while a search term is being typed); `loadMore` appends the next page — wire it to
 * a virtualized list's `onEndReached` so a 5,000-member portal never lands in one response.
 */
export function useChatDirectory(search: string, enabled = true) {
  const [users, setUsers] = useState<ChatDirectoryUser[]>([]);
  // The effect below fetches on mount whenever the hook starts out enabled, so the spinner is the
  // honest initial state for that case.
  const [loading, setLoading] = useState(enabled);
  const [hasMore, setHasMore] = useState(false);
  // Monotonic sequence: a stale response (older search, closed picker) must never land.
  const requestSeq = useRef(0);
  const loadingMoreRef = useRef(false);

  const trimmed = search.trim();
  // Null while disabled: the effect below fetches exactly once per distinct value of this key, so it
  // doubles as the latch key for `loading`.
  const requestKey = enabled ? trimmed : null;
  const [prevRequestKey, setPrevRequestKey] = useState(requestKey);

  // Render-phase latch: the spinner has to be up in the same commit that renders a new search term,
  // otherwise the previous term's results read as results for the new one. `Object.is`, because this
  // stands in for the effect deps below and React compares those the same way.
  if (!Object.is(prevRequestKey, requestKey)) {
    setPrevRequestKey(requestKey);
    setLoading(enabled);
  }

  useEffect(() => {
    if (!enabled) return undefined;
    requestSeq.current += 1;
    const seq = requestSeq.current;
    const timer = setTimeout(
      () => {
        getChatDirectoryRequest({ ...(trimmed ? { search: trimmed } : {}), skip: 0, take: DIRECTORY_PAGE_SIZE })
          .then((response) => {
            if (requestSeq.current !== seq) return;
            if (response.data.status) {
              setUsers(response.data.result ?? []);
              setHasMore(response.data.hasMore ?? false);
            }
          })
          .catch((error: unknown) => Sentry.captureException(error))
          .finally(() => {
            if (requestSeq.current === seq) setLoading(false);
          });
      },
      trimmed ? DIRECTORY_SEARCH_DEBOUNCE_MS : 0,
    );
    return () => clearTimeout(timer);
  }, [trimmed, enabled]);

  const loadMore = useCallback(() => {
    if (!enabled || !hasMore || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    const seq = requestSeq.current;
    getChatDirectoryRequest({ ...(trimmed ? { search: trimmed } : {}), skip: users.length, take: DIRECTORY_PAGE_SIZE })
      .then((response) => {
        if (requestSeq.current !== seq || !response.data.status) return;
        const page = response.data.result ?? [];
        setUsers((prev) => {
          const seen = new Set(prev.map((user) => String(user._id)));
          return [...prev, ...page.filter((user) => !seen.has(String(user._id)))];
        });
        setHasMore(response.data.hasMore ?? false);
      })
      .catch((error: unknown) => Sentry.captureException(error))
      .finally(() => {
        loadingMoreRef.current = false;
      });
  }, [enabled, hasMore, trimmed, users.length]);

  return { users, loading, hasMore, loadMore };
}
